/**
 * Resize and compress an image file for localStorage (signatures, etc.).
 * @returns {Promise<{ dataUrl: string, fileName: string }>}
 */
export const compressImageFile = (
  file,
  { maxWidth = 420, maxHeight = 180, quality = 0.72, mimeType = "image/jpeg" } = {}
) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image file."));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not process image."));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL(mimeType, quality);
        const ext = mimeType === "image/png" ? "png" : "jpg";
        const baseName = String(file.name || "signature").replace(/\.[^.]+$/, "");
        resolve({
          dataUrl,
          fileName: `${baseName}.${ext}`,
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const isNearBlackBackground = (r, g, b, a, threshold = 52) =>
  a > 10 && r <= threshold && g <= threshold && b <= threshold;

const trimTransparentBounds = (imageData, width, height) => {
  const { data } = imageData;
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      if (data[i + 3] > 12) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) return null;

  const pad = 4;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;
  const cropped = new ImageData(cropW, cropH);
  for (let y = 0; y < cropH; y += 1) {
    for (let x = 0; x < cropW; x += 1) {
      const src = ((minY + y) * width + (minX + x)) * 4;
      const dst = (y * cropW + x) * 4;
      cropped.data[dst] = data[src];
      cropped.data[dst + 1] = data[src + 1];
      cropped.data[dst + 2] = data[src + 2];
      cropped.data[dst + 3] = data[src + 3];
    }
  }
  return { imageData: cropped, width: cropW, height: cropH };
};

/**
 * Signature upload: resize, remove black/dark background, export PNG with transparency for payslip PDF.
 */
export const compressSignatureImageFile = (
  file,
  { maxWidth = 420, maxHeight = 180, backgroundThreshold = 52 } = {}
) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image file."));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not process image."));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        let imageData = ctx.getImageData(0, 0, width, height);
        const { data } = imageData;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          if (isNearBlackBackground(r, g, b, a, backgroundThreshold)) {
            data[i + 3] = 0;
          }
        }

        const trimmed = trimTransparentBounds(imageData, width, height);
        if (trimmed) {
          canvas.width = trimmed.width;
          canvas.height = trimmed.height;
          ctx.putImageData(trimmed.imageData, 0, 0);
        } else {
          ctx.putImageData(imageData, 0, 0);
        }

        const dataUrl = canvas.toDataURL("image/png");
        const baseName = String(file.name || "signature").replace(/\.[^.]+$/, "");
        resolve({
          dataUrl,
          fileName: `${baseName}.png`,
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
